import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContent, Spinner, CisContainer, Table } from 'components';
import { connect } from 'react-redux';
import {
  loadIpmaCisCreator,
  setIpmaCisPageCreator,
  setIpmaCisPerPageCreator,
  setIpmaCisSortCreator,
  setIpmaCisFiltersCreator,
  removeIpmaCisFiltersCreator,
} from 'store/actionsCreators';
import PropTypes from 'prop-types';
import { isLoading, globalPropTypes } from 'helpers';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import moment from 'moment';
import { loadGlobalCisAssessorsAttributesCreator } from '../../../../store/actionsCreators';

const Assessors = ({
  loading,
  memberAssociations,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  loadAssessorAttributes,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  useEffect(() => {
    loadAssessorAttributes();
  }, []);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  console.log('memberAssociations', memberAssociations);

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('global.id')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.assessors.memberAssociation')}
                  filter
                  filterType="select"
                  filterOptions={memberAssociations}
                  filterValue={filters.memberAssociation}
                  onFilter={(val) => handleColumnFilterChange('memberAssociation', val)}
                  filterPlaceholder={t('page.user.cis.assessors.memberAssociationPlaceholder')}
                  sort
                  sortDirection={getSortDirection('memberAssociation')}
                  onSort={(dir) => handleColumnSort('memberAssociation', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.fullName || ''}
                  onFilter={(val) => handleColumnFilterChange('fullName', val)}
                  filterPlaceholder={t('page.user.cis.assessors.fullNamePlaceholder')}
                  label={t('page.user.cis.assessors.fullName')}
                  sort
                  sortDirection={getSortDirection('fullName')}
                  onSort={(dir) => handleColumnSort('fullName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFromFrom || ''}
                  filterValueTo={filters.dateFromTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFromFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateFromTo', val)}
                  filterPlaceholder={t('page.user.cis.assessors.dateFromPlaceholder')}
                  label={t('page.user.cis.assessors.dateFrom')}
                  labelAlign="center"
                  sort
                  sortDirection={getSortDirection('dateFrom')}
                  onSort={(dir) => handleColumnSort('dateFrom', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.assessors.type')} labelAlign="center" />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.assessors.signature')}
                  labelAlign="center"
                />
              </CisTable.StickyHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('global.id')}:</Table.Label>
                      {item.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.memberAssociation')}:</Table.Label>
                      {item.memberAssociation?.name || '-'}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.fullName')}:</Table.Label>
                      {[item.user.firstName, item.user.middleName, item.user.lastName].join(' ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.dateFrom')}:</Table.Label>
                      {moment(item.dateFrom).format('L')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.type')}:</Table.Label>
                      {item.role?.label || '-'}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.assessors.signature')}:</Table.Label>
                      {item.signatureType === 'I' && (
                        <span>{t('page.user.cis.assessors.signatureTypeItalic')}</span>
                      )}
                      {(item.signatureType === 'N' || !item.signatureType) && (
                        <span>{t('page.user.cis.assessors.signatureTypeNone')}</span>
                      )}
                      {item.signatureType === 'H' &&
                        (item.hasFileUrl ? t('global.handwritten') : t('global.noPicture'))}
                    </CisTable.Cell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
    </PageContent>
  );
};

Assessors.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

Assessors.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ipma, globals }) => ({
  loading: isLoading(loading, 'LOAD_IPMA_CIS'),
  list: ipma.cis.assessors,
  memberAssociations: globals.availableMA,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadIpmaCisCreator('assessors')),
  setPage: (payload) => dispatch(setIpmaCisPageCreator('assessors', payload)),
  setPerPage: (payload) => dispatch(setIpmaCisPerPageCreator('assessors', payload)),
  setSort: (payload) => dispatch(setIpmaCisSortCreator('assessors', payload)),
  setFilters: (payload) => dispatch(setIpmaCisFiltersCreator('assessors', payload)),
  removeFilters: () => dispatch(removeIpmaCisFiltersCreator('assessors')),
  loadAssessorAttributes: () => dispatch(loadGlobalCisAssessorsAttributesCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Assessors);
